import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
  ArrowLeft,
  Download,
  FileText,
  Image,
  MapPin,
  Palette,
  PenTool,
  Save,
  Sparkles,
  Wand2,
} from 'lucide-react';
import { useInvitation } from '../contexts/InvitationContext';
import { useAuth } from '../contexts/AuthContext';
import { InvitationPreview, styleConfigs } from '../components/invitation/InvitationPreview';
import { generateInvitation, refineInvitation } from '../lib/invitationService';

const exportTargets = {
  pdf: { width: 874, height: 1240 },
  digital: { width: 1080, height: 1350 },
  story: { width: 1080, height: 1920 },
};

const styleOptions = Object.keys(styleConfigs);
const desiredLengths = ['short', 'medium', 'long'];
const themeOptions = ['Garden romance', 'Editorial luxury', 'Modern minimalist', 'Classic black tie', 'African luxury'];

function splitCommaValues(value) {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function ExportOption({ icon, title, description, onClick }) {
  return (
    <button
      onClick={onClick}
      className="flex w-full items-center gap-4 rounded-[28px] border border-[#E8DED4] bg-white px-5 py-5 text-left transition hover:-translate-y-0.5 hover:border-[#D4AF37] hover:shadow-[0_20px_45px_-28px_rgba(212,175,55,0.5)]"
    >
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#FBF5EC] text-[#B8931D]">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-[#2D241E]">{title}</p>
        <p className="mt-1 text-sm text-[#7B7066]">{description}</p>
      </div>
    </button>
  );
}

function buildOriginalInputs(invitation) {
  return {
    invitation_id: invitation.id || invitation.invitation_id || null,
    couple_names: invitation.couple_names,
    wedding_date: invitation.date || null,
    wedding_time: invitation.time || null,
    time_of_day: invitation.time_of_day || null,
    venue: invitation.location || null,
    city_country: invitation.city_country || invitation.location || null,
    event_type: invitation.event_type || 'Wedding',
    tone: invitation.tone || null,
    theme: invitation.theme || null,
    cultural_influence: invitation.cultural_influence || null,
    dress_code: invitation.dress_code || null,
    desired_length: invitation.desired_length || 'medium',
    color_preferences: invitation.color_preferences || [],
    style_preferences: invitation.style_preferences?.length ? invitation.style_preferences : [invitation.style],
    audience_style: invitation.audience_style || null,
    extra_notes: invitation.extra_notes || null,
  };
}

function completeInvitationState(invitation, generated) {
  return {
    ...invitation,
    ai_enabled: true,
    original_inputs: buildOriginalInputs(invitation),
    final_invitation_json: generated,
    selected_version_id: generated.version_id,
  };
}

export default function CreatePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const previewRef = useRef(null);
  const exportRef = useRef(null);
  const { isAuthenticated } = useAuth();
  const {
    currentInvitation,
    setCurrentInvitation,
    updateCurrentInvitation,
    updateCoupleNames,
    loadInvitation,
    saveCurrentInvitation,
    isCrafting,
    setIsCrafting,
    generatedDesign,
    setGeneratedDesign,
    getSaveLimit,
  } = useInvitation();

  const [refinementText, setRefinementText] = useState('');
  const [isRefining, setIsRefining] = useState(false);
  const [exportFormat, setExportFormat] = useState('pdf');
  const [colorInput, setColorInput] = useState((currentInvitation.color_preferences || []).join(', '));
  const [stylePreferenceInput, setStylePreferenceInput] = useState((currentInvitation.style_preferences || []).join(', '));
  const [generatorError, setGeneratorError] = useState('');

  useEffect(() => {
    setColorInput((currentInvitation.color_preferences || []).join(', '));
    setStylePreferenceInput((currentInvitation.style_preferences || []).join(', '));
  }, [currentInvitation.color_preferences, currentInvitation.style_preferences]);

  useEffect(() => {
    const editingInvitationId = location.state?.invitationId;

    if (!editingInvitationId) {
      return;
    }

    if (currentInvitation.id === editingInvitationId) {
      return;
    }

    loadInvitation(editingInvitationId);
  }, [location.state, currentInvitation.id, loadInvitation]);

  const completionCount = useMemo(() => {
    const checks = [
      currentInvitation.couple_names?.name1,
      currentInvitation.couple_names?.name2,
      currentInvitation.date,
      currentInvitation.location,
      currentInvitation.dress_code,
      currentInvitation.style,
      currentInvitation.theme,
      currentInvitation.tone,
    ];
    return checks.filter(Boolean).length;
  }, [currentInvitation]);

  const completionPercent = Math.round((completionCount / 8) * 100);
  const designReady = Boolean(generatedDesign);
  const alternatives = generatedDesign?.alternatives || [];

  const requireAuthenticatedExport = () => {
    if (isAuthenticated) {
      return true;
    }

    toast.error('Sign in or create an account to download invitation formats.');
    navigate('/create', { state: { showAuth: true, fromExport: true } });
    return false;
  };

  const prepareExportTarget = async (format) => {
    setExportFormat(format);
    await new Promise((resolve) => setTimeout(resolve, 50));
    return exportRef.current || previewRef.current;
  };

  const exportAsPNG = async (format) => {
    if (!requireAuthenticatedExport()) return;
    toast.loading('Generating PNG...');
    try {
      const target = await prepareExportTarget(format);
      if (!target) return;
      const canvas = await html2canvas(target, { scale: 2, backgroundColor: null, useCORS: true });
      const link = document.createElement('a');
      link.download = `invitation-${format}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.dismiss();
      toast.success('PNG downloaded');
    } catch (error) {
      toast.dismiss();
      toast.error('Export failed');
    }
  };

  const exportAsPDF = async () => {
    if (!requireAuthenticatedExport()) return;
    toast.loading('Generating PDF...');
    try {
      const target = await prepareExportTarget('pdf');
      if (!target) return;
      const canvas = await html2canvas(target, { scale: 2, backgroundColor: null, useCORS: true });
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a5' });
      const imgData = canvas.toDataURL('image/png');
      pdf.addImage(imgData, 'PNG', 0, 0, pdf.internal.pageSize.getWidth(), pdf.internal.pageSize.getHeight());
      pdf.save('invitation-a5.pdf');
      toast.dismiss();
      toast.success('PDF downloaded');
    } catch (error) {
      toast.dismiss();
      toast.error('Export failed');
    }
  };

  const handleSave = async () => {
    const result = await saveCurrentInvitation();
    if (result.success) {
      if (result.storage === 'local') {
        toast.success(
          result.action === 'updated'
            ? 'Invitation updated in your local library while the server is unavailable.'
            : 'Invitation added to your local library while the server is unavailable.'
        );
        return;
      }

      toast.success(result.action === 'updated' ? 'Invitation updated in your library' : 'Invitation added to your library');
      return;
    }

    if (result.reason === 'db_error') {
      toast.error(result.message || 'Unable to save this invitation right now.');
      return;
    }

    const limit = getSaveLimit();
    toast.error(`Library limit reached (${limit.current}/${limit.max}). Upgrade to Heirloom for more storage.`);
  };

  const handleGenerate = async () => {
    if (!currentInvitation.couple_names?.name1 || !currentInvitation.couple_names?.name2 || !currentInvitation.date) {
      toast.error('Add both partner names and a wedding date first.');
      return;
    }

    const requestPayload = {
      original_inputs: buildOriginalInputs(currentInvitation),
      include_alternatives: true,
    };

    setGeneratorError('');
    setIsCrafting(true);
    toast.loading('Composing your invitation direction...');

    try {
      const response = await generateInvitation(requestPayload);
      const nextInvitation = completeInvitationState(currentInvitation, response.invitation);
      setGeneratedDesign(response.invitation);
      setCurrentInvitation(nextInvitation);
      toast.dismiss();
      toast.success('AI invitation generated');
    } catch (error) {
      toast.dismiss();
      const message = error.message || 'Generation failed';
      setGeneratorError(message);
      toast.error(message);
    } finally {
      setIsCrafting(false);
    }
  };

  const handleRefine = async () => {
    if (!generatedDesign || !refinementText.trim()) {
      toast.error('Add a refinement note first.');
      return;
    }

    setGeneratorError('');
    setIsRefining(true);
    toast.loading('Refining your invitation...');

    try {
      const response = await refineInvitation({
        invitation_id: currentInvitation.id || null,
        original_inputs: buildOriginalInputs(currentInvitation),
        previous_result: generatedDesign,
        refinement_text: refinementText.trim(),
      });

      const nextInvitation = {
        ...completeInvitationState(currentInvitation, response.invitation),
        refinement_history: [...(currentInvitation.refinement_history || []), refinementText.trim()],
      };

      setGeneratedDesign(response.invitation);
      setCurrentInvitation(nextInvitation);
      setRefinementText('');
      toast.dismiss();
      toast.success('Invitation refined');
    } catch (error) {
      toast.dismiss();
      const message = error.message || 'Refinement failed';
      setGeneratorError(message);
      toast.error(message);
    } finally {
      setIsRefining(false);
    }
  };

  const applyAlternative = (alternative) => {
    const selected = {
      ...generatedDesign,
      ...alternative,
      version_id: `alt_${Date.now()}`,
      alternatives: generatedDesign.alternatives,
    };
    setGeneratedDesign(selected);
    setCurrentInvitation(completeInvitationState(currentInvitation, selected));
    toast.success(`Applied ${alternative.label.toLowerCase()} direction`);
  };

  const handleNameChange = (field, value) => {
    if (field === 'name1') {
      updateCoupleNames(value, currentInvitation.couple_names?.name2 || '');
      return;
    }
    updateCoupleNames(currentInvitation.couple_names?.name1 || '', value);
  };

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#fffdf9_0%,#f8efe6_45%,#fdf8f1_100%)]">
      <section className="border-b border-[#EFE3D7] bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.9),transparent_34%),linear-gradient(135deg,#fdfaf5_0%,#f8ede3_52%,#fffaf4_100%)]">
        <div className="mx-auto max-w-7xl px-4 pb-10 pt-8 md:px-6 md:pb-14 md:pt-10">
          <div className="mb-8 flex items-center justify-between gap-4">
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 rounded-full border border-[#E9DDD1] bg-white/90 px-4 py-2 text-sm font-medium text-[#2D241E]"
            >
              <ArrowLeft size={16} />
              Back home
            </button>
            <button
              onClick={() => navigate('/library')}
              className="hidden rounded-full border border-[#E9DDD1] bg-white/90 px-4 py-2 text-sm font-medium text-[#2D241E] md:inline-flex"
            >
              View my invitations
            </button>
          </div>

          <div className="grid gap-8 lg:grid-cols-[1.08fr_0.92fr] lg:items-start">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#B6A79A]">VowVisionsAI Studio</p>
              <h1 className="mt-5 max-w-4xl text-[2.7rem] leading-[0.98] text-[#211B17] md:text-[4.4rem]">
                Generate a premium invitation direction, then refine it until it feels unmistakably yours.
              </h1>
              <p className="mt-6 max-w-2xl text-base leading-8 text-[#6A5E55] md:text-lg">
                The AI composes invitation wording, color palette, typography, layout, and decorative cues from your details.
                You stay in control of the final card.
              </p>
            </div>

            <div className="rounded-[34px] border border-[#E9DDD1] bg-white/85 p-5 shadow-[0_30px_80px_-48px_rgba(58,44,32,0.42)] backdrop-blur">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#B6A79A]">AI readiness</p>
                  <h2 className="mt-2 text-xl font-semibold text-[#2D241E]">Brief completeness</h2>
                </div>
                <div className="rounded-full bg-[#FBF5EC] px-4 py-2 text-sm font-semibold text-[#B8931D]">{completionPercent}%</div>
              </div>
              <div className="mt-5 h-2 overflow-hidden rounded-full bg-[#F1E6D9]">
                <div
                  className="h-full rounded-full bg-[linear-gradient(90deg,#D4AF37_0%,#E6C96E_100%)] transition-all duration-500"
                  style={{ width: `${Math.max(completionPercent, 10)}%` }}
                />
              </div>
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                <div className="rounded-[24px] border border-[#EFE4D8] bg-[#FFFCF8] p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#B6A79A]">Selected style</p>
                  <p className="mt-2 text-base font-semibold text-[#2D241E]">{currentInvitation.style}</p>
                </div>
                <div className="rounded-[24px] border border-[#EFE4D8] bg-[#FFFCF8] p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#B6A79A]">Generated state</p>
                  <p className="mt-2 text-base font-semibold text-[#2D241E]">{designReady ? 'AI direction ready' : 'Awaiting generation'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto grid max-w-7xl gap-6 px-4 py-8 md:px-6 md:py-12 xl:grid-cols-[1.05fr_0.95fr]">
        <div className="space-y-6">
          <section className="rounded-[32px] border border-[#EFE5DB] bg-white px-5 py-6 shadow-[0_25px_70px_-45px_rgba(58,44,32,0.35)] md:px-7">
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#B6A79A]">Step one</p>
            <h2 className="mt-2 text-2xl font-semibold text-[#2D241E]">Event details</h2>
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <label className="space-y-2">
                <span className="text-sm font-semibold text-[#2D241E]">First partner</span>
                <input className="w-full rounded-[22px] border border-[#E7DBCE] bg-[#FCFAF7] px-4 py-4" value={currentInvitation.couple_names?.name1 || ''} onChange={(event) => handleNameChange('name1', event.target.value)} />
              </label>
              <label className="space-y-2">
                <span className="text-sm font-semibold text-[#2D241E]">Second partner</span>
                <input className="w-full rounded-[22px] border border-[#E7DBCE] bg-[#FCFAF7] px-4 py-4" value={currentInvitation.couple_names?.name2 || ''} onChange={(event) => handleNameChange('name2', event.target.value)} />
              </label>
              <label className="space-y-2">
                <span className="text-sm font-semibold text-[#2D241E]">Wedding date</span>
                <input type="date" className="w-full rounded-[22px] border border-[#E7DBCE] bg-[#FCFAF7] px-4 py-4" value={currentInvitation.date || ''} onChange={(event) => updateCurrentInvitation('date', event.target.value)} />
              </label>
              <label className="space-y-2">
                <span className="text-sm font-semibold text-[#2D241E]">Wedding time</span>
                <input type="time" className="w-full rounded-[22px] border border-[#E7DBCE] bg-[#FCFAF7] px-4 py-4" value={currentInvitation.time || ''} onChange={(event) => updateCurrentInvitation('time', event.target.value)} />
              </label>
              <label className="space-y-2 md:col-span-2">
                <span className="text-sm font-semibold text-[#2D241E]">Venue</span>
                <input className="w-full rounded-[22px] border border-[#E7DBCE] bg-[#FCFAF7] px-4 py-4" value={currentInvitation.location || ''} onChange={(event) => updateCurrentInvitation('location', event.target.value)} placeholder="Paarl, South Africa" />
              </label>
              <label className="space-y-2">
                <span className="text-sm font-semibold text-[#2D241E]">Dress code</span>
                <input className="w-full rounded-[22px] border border-[#E7DBCE] bg-[#FCFAF7] px-4 py-4" value={currentInvitation.dress_code || ''} onChange={(event) => updateCurrentInvitation('dress_code', event.target.value)} placeholder="Black Tie" />
              </label>
              <label className="space-y-2">
                <span className="text-sm font-semibold text-[#2D241E]">Cultural influence</span>
                <input className="w-full rounded-[22px] border border-[#E7DBCE] bg-[#FCFAF7] px-4 py-4" value={currentInvitation.cultural_influence || ''} onChange={(event) => updateCurrentInvitation('cultural_influence', event.target.value)} placeholder="South African, modern luxury" />
              </label>
            </div>
          </section>

          <section className="rounded-[32px] border border-[#EFE5DB] bg-white px-5 py-6 shadow-[0_25px_70px_-45px_rgba(58,44,32,0.35)] md:px-7">
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#B6A79A]">Step two</p>
            <h2 className="mt-2 text-2xl font-semibold text-[#2D241E]">Creative direction</h2>
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <label className="space-y-2">
                <span className="text-sm font-semibold text-[#2D241E]">Base style</span>
                <select className="w-full rounded-[22px] border border-[#E7DBCE] bg-[#FCFAF7] px-4 py-4" value={currentInvitation.style} onChange={(event) => updateCurrentInvitation('style', event.target.value)}>
                  {styleOptions.map((style) => (
                    <option key={style} value={style}>{style}</option>
                  ))}
                </select>
              </label>
              <label className="space-y-2">
                <span className="text-sm font-semibold text-[#2D241E]">Theme</span>
                <select className="w-full rounded-[22px] border border-[#E7DBCE] bg-[#FCFAF7] px-4 py-4" value={currentInvitation.theme || ''} onChange={(event) => updateCurrentInvitation('theme', event.target.value)}>
                  <option value="">Choose a theme</option>
                  {themeOptions.map((theme) => (
                    <option key={theme} value={theme}>{theme}</option>
                  ))}
                </select>
              </label>
              <label className="space-y-2">
                <span className="text-sm font-semibold text-[#2D241E]">Tone</span>
                <input className="w-full rounded-[22px] border border-[#E7DBCE] bg-[#FCFAF7] px-4 py-4" value={currentInvitation.tone || ''} onChange={(event) => updateCurrentInvitation('tone', event.target.value)} placeholder="Premium but romantic" />
              </label>
              <label className="space-y-2">
                <span className="text-sm font-semibold text-[#2D241E]">Desired wording length</span>
                <select className="w-full rounded-[22px] border border-[#E7DBCE] bg-[#FCFAF7] px-4 py-4" value={currentInvitation.desired_length || 'medium'} onChange={(event) => updateCurrentInvitation('desired_length', event.target.value)}>
                  {desiredLengths.map((length) => (
                    <option key={length} value={length}>{length}</option>
                  ))}
                </select>
              </label>
              <label className="space-y-2 md:col-span-2">
                <span className="text-sm font-semibold text-[#2D241E]">Color preferences</span>
                <input
                  className="w-full rounded-[22px] border border-[#E7DBCE] bg-[#FCFAF7] px-4 py-4"
                  value={colorInput}
                  onChange={(event) => {
                    setColorInput(event.target.value);
                    updateCurrentInvitation('color_preferences', splitCommaValues(event.target.value));
                  }}
                  placeholder="ivory, soft gold, espresso"
                />
              </label>
              <label className="space-y-2 md:col-span-2">
                <span className="text-sm font-semibold text-[#2D241E]">Style preferences</span>
                <input
                  className="w-full rounded-[22px] border border-[#E7DBCE] bg-[#FCFAF7] px-4 py-4"
                  value={stylePreferenceInput}
                  onChange={(event) => {
                    setStylePreferenceInput(event.target.value);
                    updateCurrentInvitation('style_preferences', splitCommaValues(event.target.value));
                  }}
                  placeholder="editorial, timeless, minimal"
                />
              </label>
              <label className="space-y-2 md:col-span-2">
                <span className="text-sm font-semibold text-[#2D241E]">Extra notes for the AI</span>
                <textarea
                  className="min-h-[120px] w-full rounded-[22px] border border-[#E7DBCE] bg-[#FCFAF7] px-4 py-4"
                  value={currentInvitation.extra_notes || ''}
                  onChange={(event) => updateCurrentInvitation('extra_notes', event.target.value)}
                  placeholder="Make it feel premium but romantic, with restrained gold and a South African luxury influence."
                />
              </label>
            </div>
          </section>
        </div>

        <div className="space-y-6 xl:sticky xl:top-24 xl:self-start">
          <section className="rounded-[36px] border border-[#E9DDD1] bg-[linear-gradient(180deg,#fffdf9_0%,#fff8f0_100%)] p-4 shadow-[0_30px_80px_-46px_rgba(58,44,32,0.35)] md:p-6">
            <div className="flex items-center justify-between gap-4 border-b border-[#EFE4D8] pb-4">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#B6A79A]">Structured preview</p>
                <h3 className="mt-2 text-2xl font-semibold text-[#2D241E]">AI invitation renderer</h3>
              </div>
              <div className="rounded-full border border-[#EFE2D0] bg-white px-4 py-2 text-xs font-semibold text-[#8A7766]">
                {designReady ? 'AI ready' : 'Awaiting generation'}
              </div>
            </div>

            <div className="mt-6 flex justify-center">
              <InvitationPreview
                ref={previewRef}
                invitation={currentInvitation}
                design={generatedDesign}
                size="large"
                isCrafting={isCrafting || isRefining}
                exportFormat={exportFormat}
              />
            </div>

            {generatedDesign ? (
              <div className="mt-6 grid gap-3 md:grid-cols-2">
                <div className="rounded-[24px] border border-[#EFE4D8] bg-white p-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-[#2D241E]">
                    <Palette size={16} className="text-[#B8931D]" />
                    Palette
                  </div>
                  <div className="mt-3 flex gap-2">
                    {Object.values(generatedDesign.palette || {}).map((color) => (
                      <span key={color} className="h-9 w-9 rounded-full border border-[#E8DED4]" style={{ backgroundColor: color }} />
                    ))}
                  </div>
                </div>
                <div className="rounded-[24px] border border-[#EFE4D8] bg-white p-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-[#2D241E]">
                    <PenTool size={16} className="text-[#B8931D]" />
                    AI direction
                  </div>
                  <p className="mt-3 text-sm text-[#6F645B]">{generatedDesign.layout}</p>
                  <p className="mt-1 text-sm text-[#6F645B]">{generatedDesign.ornament}</p>
                  <p className="mt-1 text-sm text-[#6F645B]">{generatedDesign.fonts?.heading} + {generatedDesign.fonts?.body}</p>
                </div>
              </div>
            ) : null}
          </section>

          <section className="rounded-[32px] border border-[#EFE5DB] bg-white px-5 py-6 shadow-[0_25px_70px_-45px_rgba(58,44,32,0.35)] md:px-7">
            <h3 className="text-xl font-semibold text-[#2D241E]">Generate, refine, and save</h3>
            {location.state?.mode === 'edit' ? (
              <div className="mt-4 rounded-[24px] border border-[#E9DDD1] bg-[#FFFCF8] px-4 py-3 text-sm leading-7 text-[#6F645B]">
                Editing your saved invitation. Update the details, regenerate if needed, or refine the existing invitation below.
              </div>
            ) : null}
            {generatorError ? (
              <div className="mt-5 rounded-[24px] border border-[#F2C7C2] bg-[#FFF4F2] px-4 py-4 text-sm leading-7 text-[#A94D45]">
                {generatorError}
              </div>
            ) : null}
            <div className="mt-6 grid gap-3 md:grid-cols-2">
              <button onClick={handleGenerate} disabled={isCrafting} className="flex items-center justify-center gap-3 rounded-[24px] bg-[#2B2520] px-5 py-4 text-sm font-semibold text-white disabled:opacity-70">
                <Wand2 size={18} />
                {isCrafting ? 'Generating...' : 'Generate invitation'}
              </button>
              <button onClick={handleSave} disabled={!designReady} className="flex items-center justify-center gap-3 rounded-[24px] border border-[#E6DACD] bg-[#FCFAF7] px-5 py-4 text-sm font-semibold text-[#2D241E] disabled:opacity-70">
                <Save size={18} />
                Add to library
              </button>
            </div>

            <div className="mt-5 rounded-[24px] border border-[#EEE2D6] bg-[#FFFCF8] p-4">
              <label className="block text-sm font-semibold text-[#2D241E]">Refine the result</label>
              <textarea
                className="mt-3 min-h-[100px] w-full rounded-[20px] border border-[#E7DBCE] bg-white px-4 py-3"
                value={refinementText}
                onChange={(event) => setRefinementText(event.target.value)}
                placeholder="Make it more elegant, less floral, more modern luxury, shorter wording, or more African luxury."
              />
              <button onClick={handleRefine} disabled={!generatedDesign || !refinementText.trim() || isRefining} className="mt-3 inline-flex items-center justify-center rounded-full bg-[#1F1A17] px-5 py-3 text-sm font-semibold text-white disabled:opacity-70">
                {isRefining ? 'Refining...' : 'Refine invitation'}
              </button>
            </div>

            {alternatives.length ? (
              <div className="mt-5">
                <p className="text-sm font-semibold text-[#2D241E]">Alternative directions</p>
                <div className="mt-3 space-y-3">
                  {alternatives.map((alternative) => (
                    <button key={alternative.label} onClick={() => applyAlternative(alternative)} className="flex w-full items-start justify-between rounded-[22px] border border-[#E7DBCE] bg-[#FCFAF7] px-4 py-4 text-left transition hover:border-[#D4AF37]">
                      <div>
                        <p className="text-sm font-semibold text-[#2D241E]">{alternative.label}</p>
                        <p className="mt-1 text-sm text-[#6F645B]">{alternative.layout}</p>
                      </div>
                      <span className="text-xs uppercase tracking-[0.18em] text-[#B8931D]">{alternative.tone}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="mt-6 space-y-3">
              <ExportOption icon={<FileText size={20} />} title="Print PDF" description="A5 formatted export for printing." onClick={exportAsPDF} />
              <ExportOption icon={<Image size={20} />} title="Digital feed PNG" description="1080 x 1350 for social and email." onClick={() => exportAsPNG('digital')} />
              <ExportOption icon={<Download size={20} />} title="Story PNG" description="1080 x 1920 for story placement." onClick={() => exportAsPNG('story')} />
            </div>
          </section>

          {generatedDesign ? (
            <section className="rounded-[32px] border border-[#EFE5DB] bg-white px-5 py-6 shadow-[0_25px_70px_-45px_rgba(58,44,32,0.35)] md:px-7">
              <h3 className="text-xl font-semibold text-[#2D241E]">AI output summary</h3>
              <div className="mt-4 space-y-3 text-sm leading-7 text-[#6F645B]">
                <p><span className="font-semibold text-[#2D241E]">Headline:</span> {generatedDesign.headline}</p>
                <p><span className="font-semibold text-[#2D241E]">Body:</span> {generatedDesign.body}</p>
                <p><span className="font-semibold text-[#2D241E]">Closing:</span> {generatedDesign.closing}</p>
                <p><span className="font-semibold text-[#2D241E]">RSVP:</span> {generatedDesign.rsvp}</p>
                <p><span className="font-semibold text-[#2D241E]">Tags:</span> {(generatedDesign.styleTags || []).join(', ')}</p>
                {generatedDesign.imagePrompt ? <p><span className="font-semibold text-[#2D241E]">Background art prompt:</span> {generatedDesign.imagePrompt}</p> : null}
              </div>
            </section>
          ) : null}
        </div>
      </div>

      {generatedDesign ? (
        <div className="pointer-events-none fixed left-[-9999px] top-0" aria-hidden="true">
          <div ref={exportRef} style={{ width: `${exportTargets[exportFormat].width}px`, height: `${exportTargets[exportFormat].height}px` }}>
            <InvitationPreview invitation={currentInvitation} design={generatedDesign} size="exportFull" exportFormat={exportFormat} />
          </div>
        </div>
      ) : null}
    </div>
  );
}
